import { useState, useMemo, useEffect } from 'react'
import { supabase } from '../../supabaseClient'
import { C, st, formatDate } from '../../theme'
import { Modal, ScrollTable, PageHeader, EmptyState, StatCard, Pagination, PAGE_SIZE, SearchSelect } from '../../components/UI'
import QRScanModal from '../../components/QRScanModal'
import PackageLabels from '../../components/PackageLabels'
import { useAppPrefs } from '../../AppPrefs'
import { makeT } from '../../i18n'

// ── Standalone Product Form (must be outside WMS to prevent re-mount on state change) ──
function ProductFormBody({ form, setForm }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
      <div style={{ gridColumn: 'span 2', padding: '8px 14px', background: C.accentDim, borderRadius: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.accent }}>基本資料 · Basic Info</div>
      </div>
      <div>
        <label style={st.label}>商品名稱 *</label>
        <input style={st.input} value={form.name || ''} placeholder="商品名稱" onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
      </div>
      <div>
        <label style={st.label}>SKU 編號 *</label>
        <input style={st.input} value={form.sku || ''} placeholder="SKU-001" onChange={e => setForm(f => ({ ...f, sku: e.target.value }))} />
      </div>
      <div>
        <label style={st.label}>分類</label>
        <input style={st.input} value={form.category || ''} placeholder="原料、成品..." onChange={e => setForm(f => ({ ...f, category: e.target.value }))} />
      </div>
      <div>
        <label style={st.label}>單位</label>
        <input style={st.input} value={form.unit || ''} placeholder="個、箱、kg" onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} />
      </div>
      <div>
        <label style={st.label}>條碼</label>
        <input style={st.input} value={form.barcode || ''} placeholder="1234567890" onChange={e => setForm(f => ({ ...f, barcode: e.target.value }))} />
      </div>
      <div>
        <label style={st.label}>規格</label>
        <input style={st.input} value={form.spec || ''} placeholder="尺寸、顏色..." onChange={e => setForm(f => ({ ...f, spec: e.target.value }))} />
      </div>
      <div>
        <label style={st.label}>重量 (kg)</label>
        <input type="number" min="0" style={st.input} value={form.weight || ''} placeholder="0.00" onChange={e => setForm(f => ({ ...f, weight: e.target.value }))} />
      </div>
      <div style={{ gridColumn: 'span 2' }}>
        <label style={st.label}>圖片網址 · Image URL</label>
        <input style={st.input} value={form.image_url || ''} placeholder="https://..." onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))} />
      </div>
      <div style={{ gridColumn: 'span 2', padding: '8px 14px', background: C.greenBg, borderRadius: 8, marginTop: 4 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.green }}>庫存設定 · Stock</div>
      </div>
      <div>
        <label style={st.label}>初始庫存</label>
        <input type="number" min="0" style={st.input} value={form.stock || ''} placeholder="0" onChange={e => setForm(f => ({ ...f, stock: e.target.value }))} />
      </div>
      <div>
        <label style={st.label}>安全庫存</label>
        <input type="number" min="0" style={st.input} value={form.safe_stock || ''} placeholder="0" onChange={e => setForm(f => ({ ...f, safe_stock: e.target.value }))} />
      </div>
      <div>
        <label style={st.label}>最小訂購量</label>
        <input type="number" min="0" style={st.input} value={form.min_order_qty || ''} placeholder="1" onChange={e => setForm(f => ({ ...f, min_order_qty: e.target.value }))} />
      </div>
      <div>
        <label style={st.label}>前置天數</label>
        <input type="number" min="0" style={st.input} value={form.lead_time || ''} placeholder="0" onChange={e => setForm(f => ({ ...f, lead_time: e.target.value }))} />
      </div>
      <div style={{ gridColumn: 'span 2', padding: '8px 14px', background: C.yellowBg, borderRadius: 8, marginTop: 4 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.yellow }}>價格資料 · Pricing</div>
      </div>
      <div>
        <label style={st.label}>成本價 (NT$)</label>
        <input type="number" min="0" style={st.input} value={form.cost_price || ''} placeholder="0" onChange={e => setForm(f => ({ ...f, cost_price: e.target.value }))} />
      </div>
      <div>
        <label style={st.label}>售價 (NT$)</label>
        <input type="number" min="0" style={st.input} value={form.sell_price || ''} placeholder="0" onChange={e => setForm(f => ({ ...f, sell_price: e.target.value }))} />
      </div>
      <div>
        <label style={st.label}>狀態</label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderRadius: 10, cursor: 'pointer', background: form.is_active ? C.greenBg : '#f1f5f9', border: `2px solid ${form.is_active ? C.greenBorder : C.border}`, minHeight: 52 }}>
          <input type="checkbox" checked={!!form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} style={{ width: 18, height: 18 }} />
          <span style={{ fontSize: 14, fontWeight: 700, color: form.is_active ? C.green : C.muted }}>{form.is_active ? '✅ 啟用' : '⏸ 停用'}</span>
        </label>
      </div>
    </div>
  )
}

export default function WMS({ products, records, userProfile, onRefresh, showToast, addLog, onNavigate, enabledModules = [], pendingScan, onClearPendingScan }) {
  const { lang } = useAppPrefs()
  const _ = makeT(lang)
  const pos = userProfile?.position || 'user'
  const isAdmin = userProfile?.role === 'admin'
  const hasWmsPerm = enabledModules.includes('wms')
  const canEdit = isAdmin || hasWmsPerm || ['factory_manager','senior_manager','section_chief','warehouse','sales_assistant'].includes(pos)
  const canDelete = isAdmin || enabledModules.includes('delete_records') || ['factory_manager','senior_manager'].includes(pos)
  const canAdjust = isAdmin || hasWmsPerm || ['factory_manager','senior_manager','warehouse'].includes(pos)

  const [activeSection, setActiveSection] = useState('transaction')
  const [showQRScan, setShowQRScan] = useState(false)

  // 自動開啟掃描 Modal（手機相機掃 QR → 帶著 pendingScan 進來）
  useEffect(() => {
    if (pendingScan && (pendingScan.pid || pendingScan.lid)) {
      setShowQRScan(true)
    }
  }, [pendingScan])
  const [saving, setSaving] = useState(false)

  const [createShipment, setCreateShipment] = useState(false)
  const [shipCustomer, setShipCustomer] = useState('')
  const [drivers, setDrivers] = useState([])
  const [shipDriverId, setShipDriverId] = useState('')
  const [shipDriverName, setShipDriverName] = useState('')
  const [shipPlate, setShipPlate] = useState('')

  // ── Transaction state ──
  const [form, setForm] = useState({ type: 'in', productId: '', qty: '', note: '', outDate: new Date().toISOString().slice(0, 10) })
  const [adjusting, setAdjusting] = useState(null)
  const [adjustQty, setAdjustQty] = useState('')
  const [adjustNote, setAdjustNote] = useState('')

  // ── Records state ──
  const [filterType, setFilterType] = useState('all')
  const [filterProduct, setFilterProduct] = useState('')
  const [filterCustomer, setFilterCustomer] = useState('')
  const [filterDateFrom, setFilterDateFrom] = useState('')
  const [filterDateTo, setFilterDateTo] = useState('')
  const [recordPage, setRecordPage] = useState(1)
  const [selectedRecords, setSelectedRecords] = useState([])

  // ── Record edit state ──
  const [editingRecord, setEditingRecord] = useState(null)
  const [editNote, setEditNote] = useState('')
  const [editCustomer, setEditCustomer] = useState('')
  const [editQty, setEditQty] = useState('')
  const [editReason, setEditReason] = useState('')
  const [editSaving, setEditSaving] = useState(false)

  // ── Inventory tab state ──
  const [inventorySearch, setInventorySearch] = useState('')
  const [inventoryFilterCat, setInventoryFilterCat] = useState('all')
  const [inventoryPage, setInventoryPage] = useState(1)

  // ── Batch stock-in state ──
  const [batchItems, setBatchItems] = useState([{ productId: '', qty: '', note: '' }])

  // ── Pending shipments state ──
  const [pendingShipments, setPendingShipments] = useState([])

  // ── Pending stock-in state ──
  const [pendingOrders, setPendingOrders] = useState([])
  const [stockInForm, setStockInForm] = useState({ orderId: null, productId: '', qty: '', note: '' })
  const [autoStockInLogs, setAutoStockInLogs] = useState([])
  const [selectedLogs, setSelectedLogs] = useState([])

  const { lowStock, totalItems } = useMemo(() => ({
    lowStock: products.filter(p => p.stock <= p.safe_stock),
    totalItems: products.reduce((a, b) => a + b.stock, 0),
  }), [products])

  const filteredRecords = useMemo(() => records.filter(r => {
    if (filterType !== 'all' && r.type !== filterType) return false
    if (filterProduct && !r.products?.name?.includes(filterProduct)) return false
    if (filterCustomer && !r.customer_name?.includes(filterCustomer)) return false
    if (filterDateFrom && r.created_at < filterDateFrom) return false
    if (filterDateTo && r.created_at > filterDateTo + 'T23:59:59') return false
    return true
  }), [records, filterType, filterProduct, filterCustomer, filterDateFrom, filterDateTo])

  // ── Inventory tab derived ──
  const inventoryCategories = useMemo(() =>
    ['all', ...new Set(products.map(p => p.category || '未分類'))]
  , [products])

  const filteredInventory = useMemo(() => products.filter(p => {
    if (inventorySearch && !p.name?.includes(inventorySearch) && !p.sku?.includes(inventorySearch)) return false
    if (inventoryFilterCat !== 'all' && (p.category || '未分類') !== inventoryFilterCat) return false
    return true
  }), [products, inventorySearch, inventoryFilterCat])

  const inventoryTotalPages = useMemo(() => Math.ceil(filteredInventory.length / PAGE_SIZE), [filteredInventory])
  const pagedInventory = useMemo(() => filteredInventory.slice((inventoryPage-1)*PAGE_SIZE, inventoryPage*PAGE_SIZE), [filteredInventory, inventoryPage])

  const inventoryTotalValue = useMemo(() =>
    filteredInventory.reduce((a, p) => a + (p.stock || 0) * (p.cost_price || 0), 0)
  , [filteredInventory])

  const totalRecordPages = useMemo(() => Math.ceil(filteredRecords.length / PAGE_SIZE), [filteredRecords])
  const pagedRecords = useMemo(() => filteredRecords.slice((recordPage-1)*PAGE_SIZE, recordPage*PAGE_SIZE), [filteredRecords, recordPage])

  // SearchSelect options — memoized so JSX map() doesn't rebuild on every render
  const productOptions = useMemo(() =>
    products.map(p => ({ value: p.id, label: p.name, sub: `庫存: ${p.stock} ${p.unit} · SKU: ${p.sku}` }))
  , [products])

  const productOptionsSimple = useMemo(() =>
    products.map(p => ({ value: p.id, label: p.name, sub: `現有: ${p.stock} ${p.unit}` }))
  , [products])

  const recentRecords = useMemo(() => records.slice(0, 10), [records])

  const loadExtra = async () => {
    const cid = userProfile?.company_id
    let shipQ = supabase.from('shipments').select('*, shipment_items(*)').eq('status', 'pending').order('created_at', { ascending: false }).limit(200)
    let woQ = supabase.from('work_orders').select('*').eq('status', 'done').eq('stock_in_mode', 'manual').order('updated_at', { ascending: false }).limit(200)
    let recQ = supabase.from('records').select('*, products(name, unit)').ilike('note', '%自動入庫%').order('created_at', { ascending: false }).limit(50)
    let drvQ = supabase.from('drivers').select('*').eq('is_active', true).order('name')
    if (cid) { shipQ = shipQ.eq('company_id', cid); woQ = woQ.eq('company_id', cid); recQ = recQ.eq('company_id', cid); drvQ = drvQ.eq('company_id', cid) }
    const [{ data: ships }, { data: orders }, { data: logs }, { data: drvs }] = await Promise.all([
      shipQ, woQ, recQ, drvQ,
    ])
    if (ships) setPendingShipments(ships)
    if (orders) setPendingOrders(orders)
    if (logs) setAutoStockInLogs(logs)
    if (drvs) setDrivers(drvs)
  }

  useEffect(() => { loadExtra() }, [])

  // ── Transaction ──
  const handleTransaction = async () => {
    if (!form.productId || !form.qty) { showToast('請選擇商品並填寫數量', 'error'); return }
    const qty = parseInt(form.qty)
    if (qty <= 0) { showToast('數量必須大於 0', 'error'); return }
    const product = products.find(p => p.id === form.productId)
    if (!product) return
    if (form.type === 'out' && product.stock < qty) { showToast(`庫存不足！現有 ${product.stock} ${product.unit}`, 'error'); return }
    if (form.type === 'out' && createShipment && !shipCustomer.trim()) { showToast('請填寫客戶名稱', 'error'); return }
    setSaving(true)
    const delta = form.type === 'in' ? qty : -qty
    await Promise.all([
      supabase.rpc('adjust_stock', { p_product_id: form.productId, p_delta: delta }),
      supabase.from('records').insert({ product_id: form.productId, type: form.type, qty, note: form.note, operator: userProfile?.name, customer_name: form.type === 'out' && createShipment ? shipCustomer.trim() : null, company_id: userProfile?.company_id || null }),
    ])
    // 出庫時自動建立出貨單
    if (form.type === 'out' && createShipment && shipCustomer.trim()) {
      const outDate = form.outDate || new Date().toISOString().slice(0, 10)
      const d = new Date(outDate + 'T00:00:00')
      const rocYear = d.getFullYear() - 1911
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const prefix = `SHP-${rocYear}${month}${day}`
      // 計算該日已有幾張出貨單
      const { count } = await supabase.from('shipments').select('id', { count: 'exact', head: true }).like('shipment_no', `${prefix}%`)
      const seq = String((count || 0) + 1).padStart(2, '0')
      const shipNo = `${prefix}-${seq}`
      const { data: shipData } = await supabase.from('shipments').insert({
        shipment_no: shipNo,
        customer_name: shipCustomer.trim(),
        ship_date: outDate,
        driver_id: shipDriverId || null,
        driver_name: shipDriverName,
        license_plate: shipPlate,
        status: 'shipped',
        operator: userProfile?.name,
        note: form.note,
        company_id: userProfile?.company_id || null,
      }).select().single()
      if (shipData) {
        await supabase.from('shipment_items').insert({
          shipment_id: shipData.id,
          product_id: form.productId,
          product_name: product.name,
          qty, unit: product.unit,
        })
        showToast(`✓ 出庫完成，已建立出貨單 ${shipNo}`)
      }
    } else {
      showToast(`✓ ${form.type === 'in' ? '入庫' : '出庫'} ${qty} ${product.unit}`)
    }
    await addLog(form.type === 'in' ? '入庫' : '出庫', `${product.name} ${form.type === 'in' ? '+' : '-'}${qty} ${product.unit}`)
    setForm({ type: 'in', productId: '', qty: '', note: '', outDate: new Date().toISOString().slice(0, 10) })
    setCreateShipment(false); setShipCustomer(''); setShipDriverId(''); setShipDriverName(''); setShipPlate('')
    setSaving(false)
    onRefresh(); loadExtra()
  }

  // ── Adjust stock ──
  const handleAdjustStock = async () => {
    const newQty = parseInt(adjustQty)
    if (isNaN(newQty) || newQty < 0) { showToast('請填寫正確數量', 'error'); return }
    if (!adjustNote.trim()) { showToast('請填寫盤點原因', 'error'); return }
    const diff = newQty - adjusting.stock
    setSaving(true)
    // 使用 RPC 原子更新避免競態條件（盤點以 diff 調整而非直接寫值，確保並發安全）
    await supabase.from('products').update({ stock: newQty, updated_at: new Date().toISOString() }).eq('id', adjusting.id)
    if (diff !== 0) await supabase.from('records').insert({ product_id: adjusting.id, type: diff > 0 ? 'in' : 'out', qty: Math.abs(diff), note: `📋 盤點調整 · ${adjustNote}`, operator: userProfile?.name })
    await addLog('盤點調整', `${adjusting.name} 調整為 ${newQty}（${diff >= 0 ? '+' : ''}${diff}）`)
    showToast(`✓ 庫存已調整為 ${newQty}`)
    setAdjusting(null); setAdjustQty(''); setAdjustNote('')
    setSaving(false)
    onRefresh()
  }

  // ── Records ──
  const handleDeleteRecord = async (id) => {
    if (!window.confirm('確定刪除此筆紀錄？')) return
    await supabase.from('records').delete().eq('id', id)
    showToast('✓ 已刪除'); onRefresh()
  }
  const handleBatchDeleteRecords = async () => {
    if (!window.confirm(`確定刪除 ${selectedRecords.length} 筆？`)) return
    await supabase.from('records').delete().in('id', selectedRecords)
    showToast(`✓ 已刪除 ${selectedRecords.length} 筆`)
    setSelectedRecords([]); onRefresh()
  }
  const openEditRecord = (r) => {
    setEditingRecord(r)
    setEditNote(r.note || '')
    setEditCustomer(r.customer_name || '')
    setEditQty(String(r.qty))
    setEditReason('')
  }
  const handleSaveRecordEdit = async () => {
    const newQty = parseInt(editQty)
    if (isNaN(newQty) || newQty <= 0) { showToast('請填寫正確數量', 'error'); return }
    const qtyChanged = newQty !== editingRecord.qty
    if (qtyChanged && !editReason.trim()) { showToast('數量有變更，請填寫修改原因', 'error'); return }
    setEditSaving(true)

    const updates = {
      note: editNote.trim() || null,
      customer_name: editCustomer.trim() || null,
    }
    if (qtyChanged) {
      updates.qty = newQty
      updates.original_qty = editingRecord.original_qty ?? editingRecord.qty  // 保留第一次的原始數量
      updates.edited_by = userProfile?.name || '未知'
      updates.edited_at = new Date().toISOString()
      updates.edit_reason = editReason.trim()
    }

    const { error } = await supabase.from('records').update(updates).eq('id', editingRecord.id)
    if (error) { showToast('儲存失敗：' + error.message, 'error'); setEditSaving(false); return }

    // 數量有變 → 用 RPC 原子修正庫存
    if (qtyChanged) {
      const diff = newQty - editingRecord.qty
      // 入庫紀錄數量增加 → 庫存增加；出庫紀錄數量增加 → 庫存減少
      const stockDelta = editingRecord.type === 'in' ? diff : -diff
      await supabase.rpc('adjust_stock', { p_product_id: editingRecord.product_id, p_delta: stockDelta })
      await addLog('修正紀錄數量', `${editingRecord.products?.name || ''} ${editingRecord.original_qty ?? editingRecord.qty} → ${newQty}，原因：${editReason.trim()}，修改人：${userProfile?.name}`)
    }

    showToast(qtyChanged ? '✓ 數量已修正，庫存已同步更新' : '✓ 資料已更新')
    setEditingRecord(null)
    setEditSaving(false)
    onRefresh()
  }

  // ── Pending stock-in ──
  const handleManualStockIn = async (order) => {
    if (!stockInForm.productId) { showToast('請選擇商品', 'error'); return }
    const qty = parseInt(stockInForm.qty || order.completed_qty)
    if (!qty || qty <= 0) { showToast('請填寫數量', 'error'); return }
    const product = products.find(p => p.id === stockInForm.productId)
    if (!product) return
    setSaving(true)
    await Promise.all([
      supabase.from('products').update({ stock: product.stock + qty, updated_at: new Date().toISOString() }).eq('id', stockInForm.productId),
      supabase.from('records').insert({ product_id: stockInForm.productId, type: 'in', qty, note: `生產工單手動入庫 · Work Order ${order.order_no}${stockInForm.note ? ' · ' + stockInForm.note : ''}`, operator: userProfile?.name }),
      supabase.from('work_orders').update({ stock_in_mode: 'done' }).eq('id', order.id),
    ])
    await addLog('手動入庫', `工單 ${order.order_no} 手動入庫 ${qty} 件至 ${product.name}`)
    setStockInForm({ orderId: null, productId: '', qty: '', note: '' })
    showToast(`✓ 已入庫 ${qty} 件`); setSaving(false); loadExtra(); onRefresh()
  }

  const handleDeleteAutoLog = async (id) => {
    if (!window.confirm('確定刪除？')) return
    await supabase.from('records').delete().eq('id', id)
    showToast('✓ 已刪除'); setSelectedLogs(prev => prev.filter(s => s !== id)); loadExtra()
  }

  const handleBatchDeleteLogs = async () => {
    if (!window.confirm(`確定刪除 ${selectedLogs.length} 筆？`)) return
    await supabase.from('records').delete().in('id', selectedLogs)
    showToast(`✓ 已刪除 ${selectedLogs.length} 筆`); setSelectedLogs([]); loadExtra()
  }

  // ── Batch stock-in handler ──
  const handleBatchStockIn = async () => {
    const valid = batchItems.filter(i => i.productId && parseInt(i.qty) > 0)
    if (valid.length === 0) { showToast('請至少填寫一筆商品與數量', 'error'); return }
    const idCounts = {}
    valid.forEach(i => { idCounts[i.productId] = (idCounts[i.productId] || 0) + 1 })
    const dupIds = Object.keys(idCounts).filter(id => idCounts[id] > 1)
    if (dupIds.length > 0) {
      const dupNames = dupIds.map(id => products.find(p => p.id === id)?.name || id)
      if (!window.confirm(`以下商品重複出現：${dupNames.join('、')}。重複項目將分別入庫（不合併），確定繼續？`)) return
    }
    setSaving(true)
    await Promise.all(valid.map(async item => {
      const product = products.find(p => p.id === item.productId)
      if (!product) return
      const qty = parseInt(item.qty)
      await Promise.all([
        supabase.from('products').update({ stock: product.stock + qty, updated_at: new Date().toISOString() }).eq('id', item.productId),
        supabase.from('records').insert({ product_id: item.productId, type: 'in', qty, note: item.note || '批次入庫', operator: userProfile?.name, company_id: userProfile?.company_id || null }),
      ])
    }))
    await addLog('批次入庫', `${valid.length} 項商品入庫`)
    showToast(`✓ 批次入庫完成，共 ${valid.length} 項`)
    setBatchItems([{ productId: '', qty: '', note: '' }])
    setSaving(false)
    onRefresh()
  }

  const sections = [
    { key: 'transaction', label: _('wmsTabInOut'), en: 'In/Out' },
    { key: 'batch', label: _('wmsTabBatchIn'), en: 'Batch In' },
    { key: 'package_labels', label: _('wmsTabLabels'), en: 'Pkg Labels' },
    { key: 'inventory', label: _('wmsTabInventory'), en: 'Inventory' },
    { key: 'pending_ship', label: _('wmsTabPendingShip'), en: 'Pending Ship' },
    { key: 'stockin', label: _('wmsTabPendingIn'), en: 'Pending In' },
    { key: 'records', label: _('wmsTabRecords'), en: 'Records' },
  ]

  const formatTime = (iso) => iso ? new Date(iso).toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' }) : '-'

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
        <PageHeader title={_('warehouseTitle')} en="Warehouse Management" vi="Kho hàng" subtitle={_('wmsSubtitle')} />
        <button
          onClick={() => setShowQRScan(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px', borderRadius: 12, border: 'none', background: C.accent, color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', flexShrink: 0, marginTop: 4 }}>
          <span style={{ fontSize: 20 }}>📷</span> QR 掃描入庫
        </button>
      </div>

      {showQRScan && (
        <QRScanModal
          userProfile={userProfile}
          products={products}
          onRefresh={onRefresh}
          addLog={addLog}
          showToast={showToast}
          initialScan={pendingScan}
          onClose={() => {
            setShowQRScan(false)
            if (onClearPendingScan) onClearPendingScan()
          }}
        />
      )}

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 20 }}>
        <StatCard icon="📦" value={products.length} label="商品種類" en="Products" />
        <StatCard icon="🗃️" value={totalItems} label="總庫存量" en="Total Stock" />
        <StatCard icon="⚠️" value={lowStock.length} label="低庫存" en="Low Stock" warn={lowStock.length > 0} />
        <StatCard icon="🚚" value={pendingShipments.length} label="待出貨" en="Pending Ship" warn={pendingShipments.length > 0} />
        <StatCard icon="📋" value={records.length} label="異動紀錄" en="Stock Movements" />
      </div>

      {/* Section tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {sections.map(s => (
          <button key={s.key} style={{ ...st.btn(activeSection === s.key ? 'primary' : 'default'), padding: '10px 16px' }} onClick={() => setActiveSection(s.key)}>
            <span>{s.label}</span>
            <span style={{ fontSize: 9, opacity: 0.45, display: 'block' }}>{s.en}</span>
          </button>
        ))}
      </div>

      {/* ── 入出庫 ── */}
      {activeSection === 'transaction' && (
        <div style={st.card}>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 16 }}>🔄 入出庫登記 · Stock In / Out</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {[['in',_('wmsStockIn'),'In','success'],['out',_('wmsStockOut'),'Out','danger']].map(([v,l,e,style]) => (
              <button key={v} style={{ ...st.btn(form.type === v ? style : 'default'), flex: 1, padding: '12px' }} onClick={() => setForm(f => ({ ...f, type: v }))}>
                <span style={{ fontSize: 16 }}>{l}</span>
                <span style={{ fontSize: 10, opacity: 0.6, display: 'block' }}>{e}</span>
              </button>
            ))}
          </div>
          <label style={st.label}>商品 · Product *</label>
          <div style={{ marginBottom: form.productId ? 10 : 14 }}>
            <SearchSelect options={productOptions} value={form.productId} onChange={val => setForm(f => ({ ...f, productId: val }))} placeholder={_('wmsSearchProduct')} />
          </div>
          {form.productId && (() => {
            const p = products.find(p => p.id === form.productId)
            if (!p) return null
            const isLow = p.stock <= p.safe_stock
            const qty = parseInt(form.qty) || 0
            const afterStock = form.type === 'in' ? p.stock + qty : p.stock - qty
            return (
              <div style={{ marginBottom: 14, padding: '12px 16px', borderRadius: 10, background: isLow ? C.redBg : C.greenBg, border: `1.5px solid ${isLow ? C.redBorder : C.greenBorder}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{p.name}</div>
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>SKU: {p.sku}{p.category ? ` · ${p.category}` : ''}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 22, fontWeight: 900, color: isLow ? C.red : C.green }}>{p.stock} <span style={{ fontSize: 13, fontWeight: 400 }}>{p.unit}</span></div>
                    <div style={{ fontSize: 11, color: C.muted }}>安全庫存 {p.safe_stock} {p.unit}</div>
                  </div>
                </div>
                {qty > 0 && (
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${isLow ? C.redBorder : C.greenBorder}`, display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: C.muted }}>操作後庫存：</span>
                    <span style={{ fontWeight: 800, color: afterStock < 0 ? C.red : afterStock <= p.safe_stock ? C.yellow : C.green }}>
                      {afterStock < 0 ? _('wmsStockInsufficient') : `${afterStock} ${p.unit}`}
                    </span>
                  </div>
                )}
                {isLow && <div style={{ marginTop: 6, fontSize: 12, color: C.red, fontWeight: 600 }}>{_('wmsBelowSafeStock')}</div>}
              </div>
            )
          })()}
          <label style={st.label}>數量 · Quantity *</label>
          <input type="number" min="1" style={{ ...st.input, marginBottom: 14 }} value={form.qty} placeholder="0" onChange={e => setForm(f => ({ ...f, qty: e.target.value }))} />
          <label style={st.label}>備注 · Note</label>
          <input style={{ ...st.input, marginBottom: form.type === 'out' ? 14 : 16 }} value={form.note} placeholder="說明..." onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />

          {/* 出庫日期 */}
          {form.type === 'out' && (
            <div style={{ marginBottom: 16 }}>
              <label style={st.label}>出庫日期 · Out Date</label>
              <input
                type="date"
                style={st.input}
                value={form.outDate}
                onChange={e => setForm(f => ({ ...f, outDate: e.target.value }))}
              />
              {form.outDate !== new Date().toISOString().slice(0, 10) && (
                <div style={{ fontSize: 11, color: C.accent, marginTop: 5, fontWeight: 600 }}>
                  📅 已設定為非今日出庫，出貨單日期將使用此日期
                </div>
              )}
            </div>
          )}

          {/* Create shipment option when type is out */}
          {form.type === 'out' && (
            <div style={{ marginBottom: 16 }}>
              {enabledModules.includes('shipping') ? (
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderRadius: 10, cursor: 'pointer', background: createShipment ? C.accentDim : '#f8fafc', border: `2px solid ${createShipment ? C.accent : C.border}`, marginBottom: createShipment ? 12 : 0 }}>
                  <input type="checkbox" checked={createShipment} onChange={e => setCreateShipment(e.target.checked)} style={{ width: 18, height: 18 }} />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: createShipment ? C.accent : C.text }}>{_('wmsCreateShip')}</div>
                    <div style={{ fontSize: 12, color: C.muted }}>出庫後自動新增待出貨訂單</div>
                  </div>
                </label>
              ) : (
                <div style={{ padding: '10px 14px', borderRadius: 10, background: '#f1f5f9', border: `1.5px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 16 }}>🔒</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.muted }}>同時建立出貨單</div>
                    <div style={{ fontSize: 11, color: C.muted }}>需升級至含出貨管理的方案才能使用</div>
                  </div>
                </div>
              )}
              {createShipment && (
                <div style={{ padding: '14px 16px', background: '#f8fafc', borderRadius: 10, border: `1.5px solid ${C.accent}`, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div>
                    <label style={st.label}>{_('wmsCustomerName')} · Customer *</label>
                    <input style={st.input} value={shipCustomer} placeholder="客戶名稱" onChange={e => setShipCustomer(e.target.value)} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={st.label}>{_('wmsDriverLabel')} · Driver</label>
                      <select style={st.input} value={shipDriverId} onChange={e => {
                        const d = drivers.find(d => d.id === e.target.value)
                        setShipDriverId(e.target.value)
                        setShipDriverName(d?.name || '')
                        setShipPlate(d?.license_plate || shipPlate)
                      }}>
                        <option value="">{_('wmsSelectDriver')}</option>
                        {drivers.map(d => <option key={d.id} value={d.id}>{d.name}{d.license_plate ? ` · ${d.license_plate}` : ''}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={st.label}>{_('wmsLicensePlate')} · Plate</label>
                      <input style={st.input} value={shipPlate} placeholder="ABC-1234" onChange={e => setShipPlate(e.target.value)} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          <button style={{ ...st.btn(form.type === 'in' ? 'success' : 'danger'), width: '100%', padding: '14px', fontSize: 16 }} onClick={handleTransaction} disabled={saving}>
            <span>{saving ? _('processingBtn') : form.type === 'in' ? _('wmsConfirmStockIn') : _('wmsConfirmStockOut')}</span>
          </button>
          <div style={{ marginTop: 20, fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 10 }}>{_('wmsRecentRecords')}</div>
          {recentRecords.map(r => (
            <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${C.border}` }}>
              <div>
                <div style={{ fontSize: 14, color: C.text, fontWeight: 600 }}>{r.products?.name || '-'}</div>
                <div style={{ fontSize: 11, color: C.muted }}>{r.operator} · {formatDate(r.created_at)}</div>
              </div>
              <span style={st.badge(r.type)}>{r.type === 'in' ? `+${r.qty}` : `-${r.qty}`} {r.products?.unit}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── 批次入庫 ── */}
      {activeSection === 'batch' && (
        <div style={st.card}>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 6 }}>{_('wmsBatchInTitle')} · Batch Stock In</div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>一次入庫多項商品，適合採購到貨或盤點補充</div>
          {batchItems.map((item, i) => {
            const p = products.find(p => p.id === item.productId)
            return (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr auto', gap: 10, marginBottom: 10, alignItems: 'flex-end' }}>
                <div>
                  {i === 0 && <label style={st.label}>商品 · Product *</label>}
                  <SearchSelect
                    options={productOptions}
                    value={item.productId}
                    onChange={val => setBatchItems(prev => prev.map((it, idx) => idx === i ? { ...it, productId: val } : it))}
                    placeholder="搜尋商品..."
                  />
                </div>
                <div>
                  {i === 0 && <label style={st.label}>數量 *</label>}
                  <input
                    type="number" min="1"
                    style={{ ...st.input, borderColor: item.productId && !item.qty ? C.red : undefined }}
                    value={item.qty}
                    placeholder="0"
                    onChange={e => setBatchItems(prev => prev.map((it, idx) => idx === i ? { ...it, qty: e.target.value } : it))}
                  />
                </div>
                <div>
                  {i === 0 && <label style={st.label}>備注 · Note</label>}
                  <input
                    style={st.input}
                    value={item.note}
                    placeholder="採購單號、說明..."
                    onChange={e => setBatchItems(prev => prev.map((it, idx) => idx === i ? { ...it, note: e.target.value } : it))}
                  />
                </div>
                <button
                  style={{ ...st.btn('danger'), padding: '10px 12px', minHeight: 44, opacity: batchItems.length === 1 ? 0.35 : 1 }}
                  onClick={() => batchItems.length > 1 && setBatchItems(prev => prev.filter((_, idx) => idx !== i))}
                  disabled={batchItems.length === 1}
                  title="移除此行"
                >✕</button>
                {p && item.qty && parseInt(item.qty) > 0 && (
                  <div style={{ gridColumn: '1 / -1', fontSize: 12, color: C.muted, padding: '6px 12px', background: C.greenBg, borderRadius: 8 }}>
                    {p.name}：現有 {p.stock} {p.unit} → 入庫後 <strong style={{ color: C.green }}>{p.stock + parseInt(item.qty)} {p.unit}</strong>
                  </div>
                )}
              </div>
            )
          })}
          <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
            <button
              style={{ ...st.btn('default'), padding: '10px 18px' }}
              onClick={() => setBatchItems(prev => [...prev, { productId: '', qty: '', note: '' }])}
            >
              <span>{_('wmsAddProductRow')}</span>
              <span style={{ fontSize: 9, opacity: 0.5, display: 'block' }}>Add Row</span>
            </button>
            <button
              style={{ ...st.btn('success'), flex: 1, padding: '10px', fontSize: 15 }}
              onClick={handleBatchStockIn}
              disabled={saving}
            >
              <span>{saving ? _('processingBtn') : `${_('wmsConfirmBatchIn')} (${batchItems.filter(i => i.productId && parseInt(i.qty) > 0).length} 項)`}</span>
              <span style={{ fontSize: 10, opacity: 0.6, display: 'block' }}>{saving ? 'Processing...' : 'Confirm Batch Stock In'}</span>
            </button>
          </div>
          {batchItems.some(i => i.productId && parseInt(i.qty) > 0) && (
            <div style={{ marginTop: 16, padding: '12px 16px', background: C.accentDim, borderRadius: 10, border: `1.5px solid ${C.accent}`, fontSize: 13 }}>
              <div style={{ fontWeight: 700, color: C.accent, marginBottom: 8 }}>入庫預覽 · Preview</div>
              {batchItems.filter(i => i.productId && parseInt(i.qty) > 0).map((item, i) => {
                const p = products.find(p => p.id === item.productId)
                if (!p) return null
                return (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${C.border}` }}>
                    <span style={{ color: C.text, fontWeight: 600 }}>{p.name}</span>
                    <span style={{ color: C.green, fontWeight: 700 }}>+{item.qty} {p.unit}</span>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ── 包裝標籤 ── */}
      {activeSection === 'package_labels' && (
        <div style={st.card}>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 16 }}>🏷️ 包裝標籤管理 · Package Labels</div>
          <PackageLabels
            userProfile={userProfile}
            products={products}
            showToast={showToast}
            addLog={addLog}
          />
        </div>
      )}

      {/* ── 庫存狀況 ── */}
      {activeSection === 'inventory' && (
        <div style={st.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{_('wmsInventoryHeading')}</div>
            {inventoryTotalValue > 0 && (
              <div style={{ fontSize: 13, color: C.muted }}>
                篩選庫存總值：<strong style={{ color: C.accent }}>NT$ {inventoryTotalValue.toLocaleString()}</strong>
              </div>
            )}
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              style={{ ...st.input, width: 180 }}
              value={inventorySearch}
              placeholder={_('wmsSearchNameSku')}
              onChange={e => { setInventorySearch(e.target.value); setInventoryPage(1) }}
            />
            <select style={{ ...st.input, width: 130 }} value={inventoryFilterCat} onChange={e => { setInventoryFilterCat(e.target.value); setInventoryPage(1) }}>
              {inventoryCategories.map(c => <option key={c} value={c}>{c === 'all' ? _('wmsAllCategories') : c}</option>)}
            </select>
            {(inventorySearch || inventoryFilterCat !== 'all') && (
              <button style={{ ...st.btn('default'), padding: '8px 12px', fontSize: 12 }} onClick={() => { setInventorySearch(''); setInventoryFilterCat('all'); setInventoryPage(1) }}>
                ✕ 清除
              </button>
            )}
            <span style={{ marginLeft: 'auto', fontSize: 12, color: C.muted }}>{filteredInventory.length} 項商品</span>
          </div>

          {lowStock.length > 0 && inventoryFilterCat === 'all' && !inventorySearch && (
            <div style={{ background: C.redBg, border: `2px solid ${C.redBorder}`, borderRadius: 12, padding: '14px 16px', marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: C.red, marginBottom: 8 }}>⚠️ 低庫存警示 · {lowStock.length} 項需補貨</div>
              {lowStock.map(p => (
                <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${C.redBorder}`, fontSize: 14 }}>
                  <span style={{ color: C.text, fontWeight: 600 }}>{p.name}</span>
                  <span style={{ color: C.red, fontWeight: 700 }}>現有 {p.stock} / 安全 {p.safe_stock} {p.unit}</span>
                </div>
              ))}
            </div>
          )}

          <ScrollTable>
            <thead>
              <tr>{[['商品'],['SKU'],['分類'],['庫存'],['安全庫存'],['庫存總值'],['狀態'],['操作']].map(([zh]) => <th key={zh} style={st.th}>{zh}</th>)}</tr>
            </thead>
            <tbody>
              {pagedInventory.length === 0 ? (
                <tr><td colSpan={8}><EmptyState icon="📦" text="沒有符合條件的商品" /></td></tr>
              ) : pagedInventory.map(p => (
                <tr key={p.id}>
                  <td style={{ ...st.td, color: C.text, fontWeight: 700 }}>{p.name}</td>
                  <td style={{ ...st.td, fontFamily: 'monospace', color: C.accent, fontSize: 12 }}>{p.sku}</td>
                  <td style={st.td}>
                    {p.category ? (
                      <span style={{
                        fontSize: 11, padding: '2px 8px', borderRadius: 6, fontWeight: 700,
                        background: p.category === 'NG品/可利用' ? '#fef2f2' : p.category === '半成品' ? '#fff7ed' : '#ede9fe',
                        color: p.category === 'NG品/可利用' ? '#dc2626' : p.category === '半成品' ? '#c2410c' : '#7c3aed',
                      }}>
                        {p.category === 'NG品/可利用' ? '⚠️ ' : p.category === '半成品' ? '🔧 ' : ''}{p.category}
                      </span>
                    ) : '-'}
                  </td>
                  <td style={st.td}><span style={{ fontWeight: 800, color: p.stock <= p.safe_stock ? C.red : C.green, fontSize: 16 }}>{p.stock}</span><span style={{ fontSize: 12, color: C.muted }}> {p.unit}</span></td>
                  <td style={st.td}>{p.safe_stock} {p.unit}</td>
                  <td style={{ ...st.td, color: C.muted, fontSize: 12 }}>{p.cost_price > 0 ? `NT$ ${(p.stock * p.cost_price).toLocaleString()}` : '-'}</td>
                  <td style={st.td}><span style={st.badge(p.stock <= p.safe_stock ? 'warn' : 'ok')}>{p.stock <= p.safe_stock ? _('wmsNeedsReplenishment') : _('wmsStockNormal')}</span></td>
                  <td style={st.td}>{canAdjust && <button style={{ ...st.btn('primary'), padding: '5px 10px', fontSize: 12, minHeight: 36 }} onClick={() => { setAdjusting(p); setAdjustQty(String(p.stock)); setAdjustNote('') }}><span>{_('wmsInventoryCount')}</span></button>}</td>
                </tr>
              ))}
            </tbody>
          </ScrollTable>
          <Pagination page={inventoryPage} total={inventoryTotalPages} onChange={setInventoryPage} />
        </div>
      )}

      {/* ── 商品管理 ── */}

      {/* ── 待出貨 ── */}
      {activeSection === 'pending_ship' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{_('wmsPendingShipHeading')}</div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>以下出貨單尚未出貨，請準備備貨</div>
            </div>
            {onNavigate && (
              <button style={{ ...st.btn(enabledModules.includes('shipping') ? 'primary' : 'default'), padding: '10px 18px', opacity: enabledModules.includes('shipping') ? 1 : 0.5, cursor: enabledModules.includes('shipping') ? 'pointer' : 'not-allowed' }}
                onClick={() => enabledModules.includes('shipping') ? onNavigate('shipping') : onNavigate('__locked__shipping')}>
                <span>{enabledModules.includes('shipping') ? _('wmsGoToShipping') : '🔒 出貨管理'}</span>
                <span style={{ fontSize: 9, opacity: 0.6, display: 'block' }}>{enabledModules.includes('shipping') ? 'Go to Shipping' : '未開通'}</span>
              </button>
            )}
          </div>

          {pendingShipments.length === 0 ? (
            <div style={st.card}><EmptyState icon="✅" text={_('wmsNoShipments')} /></div>
          ) : pendingShipments.map(ship => (
            <div key={ship.id} style={{ ...st.card, marginBottom: 12, border: `2px solid ${C.yellowBorder}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: C.accent, fontFamily: 'monospace' }}>{ship.shipment_no}</div>
                  <div style={{ fontSize: 13, color: C.text, marginTop: 2 }}>{ship.customer_name}</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>建立：{formatDate(ship.created_at)}{ship.note ? ` · ${ship.note}` : ''}</div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <span style={st.badge('pending')}>⏳ 待出貨</span>
                  {onNavigate && (
                    <button style={{ ...st.btn(enabledModules.includes('shipping') ? 'success' : 'default'), padding: '6px 12px', fontSize: 12, minHeight: 36, opacity: enabledModules.includes('shipping') ? 1 : 0.5, cursor: enabledModules.includes('shipping') ? 'pointer' : 'not-allowed' }}
                      onClick={() => enabledModules.includes('shipping') ? onNavigate('shipping') : onNavigate('__locked__shipping')}>
                      <span>{enabledModules.includes('shipping') ? '前往處理' : '🔒 未開通'}</span>
                    </button>
                  )}
                  <button style={{ ...st.btn('danger'), padding: '6px 12px', fontSize: 12, minHeight: 36 }} onClick={async () => {
                    if (!window.confirm(`確定取消出貨單「${ship.shipment_no}」？`)) return
                    const { error } = await supabase.from('shipments').update({ status: 'cancelled' }).eq('id', ship.id)
                    if (error) { showToast('取消失敗', 'error'); return }
                    await addLog('取消出貨單', ship.shipment_no)
                    showToast('✓ 出貨單已取消')
                    loadExtra()
                  }}>
                    <span>✕ 取消出貨</span>
                  </button>
                </div>
              </div>
              {ship.shipment_items?.length > 0 && (
                <div style={{ background: '#f8fafc', borderRadius: 10, padding: '10px 14px' }}>
                  {ship.shipment_items.map((item, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: i < ship.shipment_items.length - 1 ? `1px solid ${C.border}` : 'none', fontSize: 13 }}>
                      <span style={{ color: C.text, fontWeight: 600 }}>{item.product_name}</span>
                      <span style={{ color: C.accent, fontWeight: 700 }}>× {item.qty} {item.unit}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </>
      )}

      {/* ── 待入庫 ── */}
      {activeSection === 'stockin' && (
        <>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 6 }}>{_('wmsPendingInHeading')}</div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>以下工單已完工，等待手動入庫</div>
          {pendingOrders.length === 0 ? (
            <div style={{ ...st.card, marginBottom: 20 }}><EmptyState icon="✅" text={_('wmsNoPendingIn')} /></div>
          ) : pendingOrders.map(order => (
            <div key={order.id} style={{ ...st.card, marginBottom: 12, border: `2px solid ${C.yellowBorder}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.accent, fontFamily: 'monospace' }}>{order.order_no}</div>
                  <div style={{ fontSize: 13, color: C.text, marginTop: 3 }}>{order.product_name}</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>數量：{order.completed_qty} 件 · 完工：{formatDate(order.updated_at)}</div>
                </div>
                <span style={st.badge('pending')}>待入庫</span>
              </div>
              {stockInForm.orderId === order.id ? (
                <div style={{ background: '#f8fafc', borderRadius: 10, padding: 14 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 12 }}>
                    <div>
                      <label style={st.label}>入庫商品 *</label>
                      <SearchSelect options={productOptionsSimple} value={stockInForm.productId} onChange={val => setStockInForm(f => ({ ...f, productId: val }))} placeholder="搜尋商品..." />
                    </div>
                    <div>
                      <label style={st.label}>數量 *</label>
                      <input type="number" min="1" style={st.input} value={stockInForm.qty} onChange={e => setStockInForm(f => ({ ...f, qty: e.target.value }))} placeholder={order.completed_qty} />
                    </div>
                    <div>
                      <label style={st.label}>備注</label>
                      <input style={st.input} value={stockInForm.note} onChange={e => setStockInForm(f => ({ ...f, note: e.target.value }))} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button style={{ ...st.btn('success'), flex: 1, padding: '10px' }} onClick={() => handleManualStockIn(order)} disabled={saving}><span>{saving ? _('processingBtn') : _('wmsConfirmStockIn')}</span></button>
                    <button style={{ ...st.btn('default'), padding: '10px 16px' }} onClick={() => setStockInForm({ orderId: null, productId: '', qty: '', note: '' })}>{_('cancel')}</button>
                  </div>
                </div>
              ) : (
                <button style={{ ...st.btn(), padding: '9px 20px' }} onClick={() => setStockInForm({ orderId: order.id, productId: order.product_id || '', qty: order.completed_qty, note: '' })}>
                  <span>{_('wmsExecuteStockIn')}</span>
                </button>
              )}
            </div>
          ))}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, marginTop: 8, flexWrap: 'wrap', gap: 8 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{_('wmsAutoStockInHeading')}</div>
              <div style={{ fontSize: 12, color: C.muted }}>由生產工單自動觸發的入庫紀錄</div>
            </div>
            {isAdmin && selectedLogs.length > 0 && (
              <button style={{ ...st.btn('danger'), padding: '8px 14px' }} onClick={handleBatchDeleteLogs}><span>🗑 刪除({selectedLogs.length})</span></button>
            )}
          </div>
          <div style={st.card}>
            {autoStockInLogs.length === 0 ? <EmptyState icon="📭" text={_('wmsNoAutoLogs')} /> : (
              <ScrollTable>
                <thead><tr>{['時間','商品','數量','操作人','工單'].map(h => <th key={h} style={st.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {autoStockInLogs.map(r => (
                    <tr key={r.id}>
                      <td style={st.td}>{formatDate(r.created_at)}</td>
                      <td style={{ ...st.td, fontWeight: 600, color: C.text }}>{r.products?.name || '-'}</td>
                      <td style={{ ...st.td, color: C.green, fontWeight: 700 }}>+{r.qty} {r.products?.unit}</td>
                      <td style={st.td}>{r.operator}</td>
                      <td style={{ ...st.td, fontSize: 11, color: C.muted }}>{r.note?.replace('生產工單自動入庫 · Work Order ', '') || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </ScrollTable>
            )}
          </div>
        </>
      )}

      {/* ── 紀錄查詢 ── */}
      {activeSection === 'records' && (
        <div style={st.card}>
          {/* Filter row 1: type */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            {['all','in','out'].map(t => (
              <button key={t} style={{ ...st.btn(filterType === t ? 'primary' : 'default'), padding: '7px 16px' }} onClick={() => { setFilterType(t); setRecordPage(1); setSelectedRecords([]) }}>
                <span>{t === 'all' ? _('filterAll') : t === 'in' ? _('wmsStockIn') : _('wmsStockOut')}</span>
              </button>
            ))}
            <span style={{ marginLeft: 'auto', fontSize: 12, color: C.muted }}>共 {filteredRecords.length} 筆</span>
            {canDelete && selectedRecords.length > 0 && (
              <button style={{ ...st.btn('danger'), padding: '7px 14px' }} onClick={handleBatchDeleteRecords}><span>🗑 刪除({selectedRecords.length})</span></button>
            )}
          </div>

          {/* Filter row 2: product + customer + date range */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center', padding: '12px 14px', background: '#f8fafc', borderRadius: 10, border: `1.5px solid ${C.border}` }}>
            <div style={{ fontSize: 12, color: C.muted, fontWeight: 700, marginRight: 4 }}>篩選：</div>
            <input
              style={{ ...st.input, width: 150 }}
              value={filterProduct}
              placeholder="🔍 商品名稱"
              onChange={e => { setFilterProduct(e.target.value); setRecordPage(1) }}
            />
            <input
              style={{ ...st.input, width: 150 }}
              value={filterCustomer}
              placeholder="🔍 客戶名稱"
              onChange={e => { setFilterCustomer(e.target.value); setRecordPage(1) }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="date" style={{ ...st.input, width: 140 }} value={filterDateFrom} onChange={e => { setFilterDateFrom(e.target.value); setRecordPage(1) }} />
              <span style={{ fontSize: 13, color: C.muted }}>～</span>
              <input type="date" style={{ ...st.input, width: 140 }} value={filterDateTo} onChange={e => { setFilterDateTo(e.target.value); setRecordPage(1) }} />
            </div>
            {(filterProduct || filterCustomer || filterDateFrom || filterDateTo) && (
              <button style={{ ...st.btn('default'), padding: '7px 12px', fontSize: 12 }} onClick={() => { setFilterProduct(''); setFilterCustomer(''); setFilterDateFrom(''); setFilterDateTo(''); setRecordPage(1) }}>
                <span>✕ 清除篩選</span>
              </button>
            )}
          </div>

          {canDelete && filteredRecords.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', borderBottom: `1px solid ${C.border}`, fontSize: 13, color: C.muted }}>
              <input type="checkbox" checked={selectedRecords.length === pagedRecords.length && pagedRecords.length > 0} onChange={e => setSelectedRecords(e.target.checked ? pagedRecords.map(r => r.id) : [])} style={{ width: 16, height: 16 }} />
              <span>全選本頁</span>
            </div>
          )}
          <ScrollTable>
            <thead>
              <tr>
                {canDelete && <th style={st.th}></th>}
                {['日期','類型','商品','數量','客戶','操作人','備注',''].map(h => <th key={h} style={st.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {pagedRecords.length === 0 ? (
                <tr><td colSpan={canDelete ? 9 : 8}><EmptyState icon="📋" text="沒有符合條件的紀錄" /></td></tr>
              ) : pagedRecords.map(r => (
                <tr key={r.id} style={{ background: selectedRecords.includes(r.id) ? C.accentDim : 'transparent' }}>
                  {canDelete && <td style={st.td}><input type="checkbox" checked={selectedRecords.includes(r.id)} onChange={e => setSelectedRecords(prev => e.target.checked ? [...prev, r.id] : prev.filter(id => id !== r.id))} style={{ width: 16, height: 16 }} /></td>}
                  <td style={st.td}>{formatDate(r.created_at)}</td>
                  <td style={st.td}><span style={st.badge(r.type)}>{r.type === 'in' ? _('wmsStockIn') : _('wmsStockOut')}</span></td>
                  <td style={{ ...st.td, color: C.text }}>{r.products?.name || '-'}</td>
                  <td style={st.td}>
                    {r.original_qty != null ? (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <span style={{ fontWeight: 800, color: C.yellow }}>{r.qty}</span>
                          <span style={{ fontSize: 12, color: C.muted }}>{r.products?.unit}</span>
                          <span title={`原始數量：${r.original_qty}　修改人：${r.edited_by}　原因：${r.edit_reason}`}
                            style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: '#fef9c3', color: '#92400e', border: '1px solid #fde047', cursor: 'help', fontWeight: 700 }}>
                            ✏️ 已修改
                          </span>
                        </div>
                        <div style={{ fontSize: 10, color: C.muted, marginTop: 1 }}>
                          原：<span style={{ textDecoration: 'line-through' }}>{r.original_qty}</span>
                        </div>
                      </div>
                    ) : (
                      <span style={{ fontWeight: 600 }}>{r.qty} <span style={{ fontWeight: 400, color: C.muted, fontSize: 12 }}>{r.products?.unit}</span></span>
                    )}
                  </td>
                  <td style={{ ...st.td, color: r.customer_name ? C.accent : C.muted, fontWeight: r.customer_name ? 600 : 400 }}>{r.customer_name || '-'}</td>
                  <td style={st.td}>{r.operator}</td>
                  <td style={{ ...st.td, color: C.muted, fontSize: 12, maxWidth: 200 }}>
                    {r.note
                      ? <span title={r.note}>{r.note}</span>
                      : <span style={{ fontStyle: 'italic', color: C.border }}>無備注</span>}
                  </td>
                  <td style={st.td}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {canEdit && (
                        <button style={{ ...st.btn('primary'), padding: '4px 10px', fontSize: 11 }} onClick={() => openEditRecord(r)}>
                          <span>✏️ 編輯</span>
                        </button>
                      )}
                      {canDelete && (
                        <button style={{ ...st.btn('danger'), padding: '4px 8px', fontSize: 11 }} onClick={() => handleDeleteRecord(r.id)}>
                          <span>刪除</span>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </ScrollTable>
          <Pagination page={recordPage} total={totalRecordPages} onChange={setRecordPage} />
        </div>
      )}

      {/* ── Record Edit Modal ── */}
      {editingRecord && (() => {
        const qtyChanged = parseInt(editQty) !== editingRecord.qty
        return (
          <Modal title={_('wmsEditRecordTitle')} onClose={() => setEditingRecord(null)}>
            {/* Record summary */}
            <div style={{ background: C.panel, borderRadius: 10, padding: '12px 16px', marginBottom: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 15, fontWeight: 800, color: C.text }}>{editingRecord.products?.name || '-'}</span>
                <span style={st.badge(editingRecord.type)}>{editingRecord.type === 'in' ? _('wmsStockIn') : _('wmsStockOut')}</span>
              </div>
              <div style={{ fontSize: 12, color: C.muted }}>
                🕐 {formatDate(editingRecord.created_at)}
                {editingRecord.operator && <span>　👤 {editingRecord.operator}</span>}
              </div>
              {editingRecord.original_qty != null && (
                <div style={{ fontSize: 12, padding: '6px 10px', background: '#fef9c3', borderRadius: 8, color: '#92400e', marginTop: 2 }}>
                  ⚠️ 此紀錄已被修改過　原始數量：<strong>{editingRecord.original_qty}</strong>　修改人：{editingRecord.edited_by}
                </div>
              )}
            </div>

            {/* 數量（可修改，需留足跡） */}
            <div style={{ marginBottom: 16 }}>
              <label style={st.label}>數量 · Quantity <span style={{ color: C.red }}>*</span></label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input
                  type="number"
                  min="1"
                  style={{ ...st.input, fontWeight: 800, fontSize: 16, maxWidth: 140,
                    borderColor: qtyChanged ? C.yellow : C.border,
                    background: qtyChanged ? '#fefce8' : undefined,
                  }}
                  value={editQty}
                  onChange={e => setEditQty(e.target.value)}
                />
                <span style={{ fontSize: 14, color: C.muted }}>{editingRecord.products?.unit}</span>
                {qtyChanged && (
                  <span style={{ fontSize: 12, color: '#92400e', fontWeight: 700 }}>
                    {editingRecord.qty} → {editQty || '?'}
                  </span>
                )}
              </div>
            </div>

            {/* 修改原因（數量變動時必填） */}
            {qtyChanged && (
              <div style={{ marginBottom: 16, padding: '12px 14px', background: '#fef9c3', borderRadius: 10, border: '1.5px solid #fde047' }}>
                <label style={{ ...st.label, color: '#92400e' }}>{_('wmsEditReasonLabel')} · Edit Reason <span style={{ color: C.red }}>*</span></label>
                <input
                  style={{ ...st.input, borderColor: '#fde047', background: '#fff' }}
                  value={editReason}
                  placeholder="請說明數量為何與原始不符，例如：輸入有誤、實際點數後更正..."
                  onChange={e => setEditReason(e.target.value)}
                  maxLength={200}
                  autoFocus
                />
                <div style={{ fontSize: 11, color: '#92400e', marginTop: 6 }}>
                  ⚠️ 數量修改將同步更新現有庫存，且修改人與原因會永久留存於紀錄中
                </div>
              </div>
            )}

            {/* 客戶名稱 */}
            <div style={{ marginBottom: 16 }}>
              <label style={st.label}>客戶名稱 · Customer</label>
              <input
                style={st.input}
                value={editCustomer}
                placeholder="輸入客戶名稱（可留空）"
                onChange={e => setEditCustomer(e.target.value)}
              />
            </div>

            {/* 備注 */}
            <div style={{ marginBottom: 20 }}>
              <label style={st.label}>備注 · Note</label>
              <textarea
                style={{ ...st.input, minHeight: 80, resize: 'vertical', fontFamily: 'inherit' }}
                value={editNote}
                placeholder="輸入備注說明..."
                onChange={e => setEditNote(e.target.value)}
                maxLength={300}
              />
              <div style={{ fontSize: 11, color: C.muted, marginTop: 4, textAlign: 'right' }}>{editNote.length} / 300</div>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                style={{ ...st.btn(qtyChanged ? 'warning' : 'success'), flex: 1, padding: '12px' }}
                onClick={handleSaveRecordEdit}
                disabled={editSaving}
              >
                <span>{editSaving ? _('savingBtn') : qtyChanged ? '⚠️ 確認修正數量' : '✓ 儲存'}</span>
              </button>
              <button style={{ ...st.btn('default'), padding: '12px 20px' }} onClick={() => setEditingRecord(null)}>
                <span>{_('cancel')}</span>
              </button>
            </div>
          </Modal>
        )
      })()}

      {/* Adjust Modal */}
      {adjusting && (
        <Modal title={`📋 盤點調整 — ${adjusting.name}`} onClose={() => { setAdjusting(null); setAdjustQty(''); setAdjustNote('') }}>
          <div style={{ padding: '12px 16px', background: '#f8fafc', borderRadius: 10, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: C.sub }}>
              <span>SKU：{adjusting.sku}</span>
              <span>{_('wmsCurrentStock')}<strong style={{ color: C.accent }}>{adjusting.stock} {adjusting.unit}</strong></span>
            </div>
          </div>
          <label style={st.label}>調整後庫存數量 *</label>
          <input type="number" min="0" style={{ ...st.input, marginBottom: 8 }} value={adjustQty} onChange={e => setAdjustQty(e.target.value)} />
          {adjustQty !== '' && !isNaN(parseInt(adjustQty)) && parseInt(adjustQty) !== adjusting.stock && (
            <div style={{ fontSize: 12, marginBottom: 14, padding: '8px 12px', borderRadius: 8, background: parseInt(adjustQty) > adjusting.stock ? C.greenBg : C.redBg, color: parseInt(adjustQty) > adjusting.stock ? C.green : C.red }}>
              {parseInt(adjustQty) > adjusting.stock ? '▲' : '▼'} 差異：{parseInt(adjustQty) > adjusting.stock ? '+' : ''}{parseInt(adjustQty) - adjusting.stock} {adjusting.unit}
            </div>
          )}
          <label style={st.label}>{_('wmsCountReason')}</label>
          <input style={{ ...st.input, marginBottom: 16 }} value={adjustNote} onChange={e => setAdjustNote(e.target.value)} placeholder="例如：定期盤點、損耗調整..." />
          <button style={{ ...st.btn('success'), width: '100%', padding: '12px' }} onClick={handleAdjustStock} disabled={saving}>
            <span>{saving ? _('processingBtn') : _('wmsConfirmAdjust')}</span>
          </button>
        </Modal>
      )}
    </div>
  )
}
